import React, { useEffect, useMemo, useState, useTransition } from "react";
import { useSelector } from "react-redux";
import { Navbar } from "../Navigation/Navbar";
import {
  DatePicker,
  Space,
  Select,
  FloatButton,
  Radio,
  Button,
  Table,
  TreeSelect,
  Spin,
  Popconfirm,
  Segmented,
  Form
} from "antd";
import { audioMessage as message } from '../Common/Notifications/audioMessage';
import "bootstrap";
import { TrainingAPI } from "../../api/TrainingAPI";
import { fp_metrics } from "../../Utils/fp_metrics";
import { downloadBlob } from "../../Utils/pdf_utils";
import { PositionSelect } from "../PositionSelect/PositionSelect";
import { HelpButton } from "../Common/HelpButton";
import { LockOutlined } from "@ant-design/icons";
import { useDataAccess } from "@components/PermissionedComponents";

const { RangePicker } = DatePicker;
const { SHOW_PARENT } = TreeSelect;
let trainingAPI = new TrainingAPI();
// Maximum amount of players we will query for any team
const TEAM_PLAYER_LIMIT = 1000;

const Leaderboard = () => {
  //Get necessary info including gpsMegrics from Redux.
  const {
    // loading: { gps: loading },
    metrics,
    positions,
    playerGroups,
    sport,
    all_metric_types,
    info: {display_name: team_name}
  } = useSelector(({ team }) => team);

  const { canAccess } = useDataAccess();

  const METRIC_TREE_STREAM = {
    gpsMetricTree: "gps",
    fpMetricTree: "forceplate",
    testingMetricTree: "testing",
    nutritionMetricTree: "nutrition",
    cognitionMetricTree: "cognition",
  };

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [filterMetrics, setFilterMetrics] = useState([]);
  // const [filterTopPlayerNumber, setFilterTopPlayerNumber] = useState(10);
  const [filterPlayerCount, setFilterPlayerCount] = useState('top_10');
  const [filterTeam, setFilterTeam] = useState();
  const [filterGroups, setFilterGroups] = useState([]);
  const [filterValue, setFilterValue] = useState("average");
  const [leaderBoardData, setLeaderBoardData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [lightMode, setLightMode] = useState('light');
  const [orientation, setOrientation] = useState('landscape');
  const [groupBy, setGroupBy] = useState(undefined);

  const top_x_options = [
    {'label': 'Top 5', 'value': 'top_5', 'num_value': 5, 'top': true},
    {'label': 'Top 10', 'value': 'top_10', 'num_value': 10, 'top': true},
    {'label': 'Top 15', 'value': 'top_15', 'num_value': 15, 'top': true},
    {'label': 'Top 25', 'value': 'top_25', 'num_value': 25, 'top': true},
    {'label': 'Top 50', 'value': 'top_50', 'num_value': 50, 'top': true},
    {'label': 'All', 'value': 'all', 'num_value': TEAM_PLAYER_LIMIT, 'top': true},
    {'label': 'Bottom 5', 'value': 'bottom_5', 'num_value': 5, 'top': false},
    {'label': 'Bottom 10', 'value': 'bottom_10', 'num_value': 10, 'top': false},
    {'label': 'Bottom 15', 'value': 'bottom_15', 'num_value': 15, 'top': false},
    {'label': 'Bottom 25', 'value': 'bottom_25', 'num_value': 25, 'top': false},
    {'label': 'Bottom 50', 'value': 'bottom_50', 'num_value': 50, 'top': false},
  ]

  let report_color_palette_options = [
  {'label': 'Light', 'value': 'light'},
  {'label': 'Dark', 'value': 'dark'}
]
if (team_name == 'Pittsburgh Steelers'){
  report_color_palette_options.push({
    'label': 'Custom', 'value': '3_30_50'
  })
}



  const fpMetrics = all_metric_types?.forceplate?.map(x => ({
    ...x,
    title: x.name,
    value: x.unique_identifier
  })); 
  
  const testingMetrics = all_metric_types?.testing?.map(x => ({
    ...x,
    title: x.name,
    value: x.unique_identifier
  }));

  const gpsMetrics = all_metric_types?.gps?.map(x => ({
    ...x,
    title: x.name,
    value: x.unique_identifier
  }));

  const nutritionMetrics = all_metric_types?.nutrition?.map(x => ({
    ...x,
    title: x.name,
    value: x.unique_identifier
  }));

  const cognitionMetrics = all_metric_types?.cognition?.map(x => ({
    ...x,
    title: x.name,
    value: x.unique_identifier
  }));


  //Generate new Object array which is formatted key: value
  const gpsDataMetrics = metrics.reduce((result, { name, key }) => {
    result[key] = name;
    return result;
  }, {});


  const totalMetrics = [
    ...all_metric_types?.gps || [],
    ...all_metric_types?.forceplate || [],
    ...all_metric_types?.testing || [],
    ...all_metric_types?.nutrition || [],
    ...all_metric_types?.cognition || [],
  ];

  //Get positions for dropdown list
  const positionsOption = positions.map((position, index) => ({
    value: position,
  }));
  
  //Get PlayerGroups for dropdown list
  const playerGroupsOption = playerGroups.map((playerGroup) => ({
    label: playerGroup.name,
    value: playerGroup.player_group_id,
  }));

  //All selected Metrics for Tree parent
  const fixedFilter = useMemo(() => {
    const fixedFilter = [...filterMetrics];
    if (fixedFilter.includes("gpsMetricTree"))
      fixedFilter.splice(
        fixedFilter.indexOf("gpsMetricTree"),
        1,
        ...gpsMetrics.map(x => x.unique_identifier)
      );
    if (fixedFilter.includes("fpMetricTree"))
      fixedFilter.splice(
        fixedFilter.indexOf("fpMetricTree"),
        1,
        ...fpMetrics.map(x => x.unique_identifier)
      );
    if (fixedFilter.includes("testingMetricTree"))
      fixedFilter.splice(
        fixedFilter.indexOf("testingMetricTree"),
        1,
        ...testingMetrics.map(x => x.unique_identifier)
      );
    if (fixedFilter.includes("nutritionMetricTree"))
      fixedFilter.splice(
        fixedFilter.indexOf("nutritionMetricTree"),
        1,
        ...nutritionMetrics.map(x => x.unique_identifier)
      );
    if (fixedFilter.includes("cognitionMetricTree"))
      fixedFilter.splice(
        fixedFilter.indexOf("cognitionMetricTree"),
        1,
        ...cognitionMetrics.map(x => x.unique_identifier)
      );
    return fixedFilter;
  }, [filterMetrics]);


  //treeData parent structure(antd)
  const treeData = [
    {
      title: "GPS Metrics",
      value: "gpsMetricTree",
      children: gpsMetrics,
    },
    {
      title: "Force Plate Metrics",
      value: "fpMetricTree",
      children: fpMetrics,
    },
    {
      title: "Testing Metrics",
      value: "testingMetricTree",
      children: testingMetrics,
    },
    {
      title: "Nutrition Metrics",
      value: "nutritionMetricTree",
      children: nutritionMetrics,
    },
    {
      title: "Cognition Metrics",
      value: "cognitionMetricTree",
      children: cognitionMetrics,
    },
  ];

  const handleDateRangeChange = (value, dateString) => {
    setStartDate(dateString?.[0]);
    setEndDate(dateString?.[1]);
  };

  const handleFilterMetrics = (metrics) => {
    setFilterMetrics(metrics);
  };

  // const handleTopPlayerOption = (topNumber) => {
  //   setFilterTopPlayerNumber(topNumber)
  //   // Fetch all players automatically if the user selects "All"
  //   // AND we already have leaderBoardData displayed
  //   if(topNumber > 25 && leaderBoardData) {
  //     const fetchAllPlayers = true
  //     handleSearch(null, fetchAllPlayers)
  //   }
  // };

  const handlePlayerCountChange = (value) => {
    setFilterPlayerCount(value);
    // Fetch all players automatically if the user selects "All"
    // AND we already have leaderBoardData displayed

    const num_value = top_x_options.find(x=>x.value == value)?.num_value || 10;

    if(num_value > 25 && leaderBoardData) {
      const fetchAllPlayers = true
      handleSearch(null, fetchAllPlayers)
    }
  };

  const handleFilterTeam = (team) => {
    setFilterTeam(team);
  };

  const handleFilterPlayerGroup = (playerGroups) => {
    setFilterGroups(playerGroups);
  };

  const handleFilterValueChange = (e) => {
    setFilterValue(e.target.value);
  };

  //Tree node structure
  const tProps = {
    treeData: treeData
      .map(({ children, ...rest }) => {
        const stream = METRIC_TREE_STREAM[rest.value];
        const locked = stream ? !canAccess(stream) : false;
        const childrenFiltered = children?.filter(
          (e) => e.visible === true || e.visible === undefined
        );
        return {
          ...rest,
          // Kept visible but locked, so the user can see the data exists and ask for it.
          title: locked ? (
            <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              {rest.title}
              <LockOutlined />
            </span>
          ) : (
            rest.title
          ),
          disabled: locked,
          selectable: !locked,
          children: locked ? [] : childrenFiltered,
        };
      })
      .filter(({ children, disabled }) => disabled || (children && children.length > 0)),
    value: filterMetrics,
    label: filterMetrics,
    onChange: handleFilterMetrics,
    treeCheckable: true,
    showCheckedStrategy: SHOW_PARENT,
    placeholder: "Metrics Selection",
    style: {
      width: 220,
    },
  };

  //Search Handler when click the search button
  const handleSearch = async (_, fetchAllPlayers = false) => {
    setIsLoading(true);
    if (!startDate || !endDate) {
      if (!startDate) message.warning("Please enter the Start Date");
      if (!endDate) message.warning("Please enter the End Date");
      return false;
    }
    try {
      const limit = filterPlayerCount
      const leaderBoardData = await trainingAPI.getLeaderBoard(
        startDate,
        endDate,
        filterTeam,
        filterGroups,
        limit,
        groupBy
      );
      setLeaderBoardData(leaderBoardData);
    } catch (err) {
      message.error("Error generating leaderBoard");
      console.log(err);
    }
    setIsLoading(false)
  };
  //Table columns structure
  const columns = [
    {
      title: "No",
      dataIndex: "no",
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Value",
      dataIndex: "value",
    },
  ];
  //Sorted by sorting metric(average, sum, max) and pop 10 top players
  const filterLeaderBoardData = (leaderBoardData, filters) => {
    if (leaderBoardData === null) {
      return [];
    }

    let result_tables = [];

    const valid_keys = (Object.keys(leaderBoardData?.data))?.filter((key) => {
      return filters.includes(key) || filters.some(x => key.startsWith(`${x}__`));
    });
    
    valid_keys.map((filterMetric) => {

      if (leaderBoardData?.data?.[filterMetric]) {
        const is_low_good = testingMetrics.concat(fpMetrics).concat(nutritionMetrics).concat(cognitionMetrics).filter(
          x=> x.unique_identifier==filterMetric)?.[0]?.low_good
        const titles = leaderBoardData?.key_to_titles?.[filterMetric] || [filterMetric];
        const title = titles[0];
        let subtitle = titles.length > 1 ? titles[1]: undefined;
        
        let arrayFilteredData = (leaderBoardData?.data?.[filterMetric] || [])
          .sort((a, b) => {
            if (is_low_good) {
              return a[filterValue] - b[filterValue];
            } else {
              return b[filterValue] - a[filterValue];
            }
          })
          .map((item, index) => {
            return {
              no: index + 1,
              name: item.full_name,
              value: item[filterValue],
            };
          })
          
          if (top_x_options.find(x=>x.value == filterPlayerCount)?.top === false){
            arrayFilteredData = arrayFilteredData.reverse()
          }
        const num_value = top_x_options.find(x=>x.value == filterPlayerCount)?.num_value || 10;
        arrayFilteredData = arrayFilteredData.slice(0, num_value);
        result_tables.push({'rows': arrayFilteredData, 'title': title, 'subtitle': subtitle});
      } 
    });
    return result_tables;
  };

  const popConfirmContent = (<>
  <span style={{fontWeight: 'bold'}}>Generate a PDF with the selected metrics and options?</span><br />
  <span style={{marginRight: '0.5em',}} >Customize your report: </span>

  <div style={{marginTop:'10px'}}>
    <span style={{marginRight: '0.5em',}} >Style: </span>
    <Segmented
              options={report_color_palette_options}
              onChange={(value) => setLightMode(value)}
              value={lightMode}
            />
        

  </div>
  <div style={{marginTop:'5px'}}>
    <span style={{marginRight: '0.5em',}} >Orientation: </span>
    <Segmented
              options={[
                {'label': 'Landscape', 'value': 'landscape'},
                {'label': 'Portrait', 'value': 'portrait'}
              ]}
              onChange={(value) => setOrientation(value)}
              value={orientation}
            />
      

  </div>

  
  </>)


  const handleExportToPDF = async () => {
    message.info("Generating PDF")
    setIsLoading(true);

    //position = undefined, player_group = undefined, metrics = undefined

    try {

      let payload = {
        gps: [],
        fp_metrics: [],
        testing: [],
        nutrition: [],
        cognition: []
      }

      filterMetrics.map(metric_uid => {
        if (gpsMetrics.filter(x=>x.unique_identifier == metric_uid).length > 0){
          payload?.gps?.push(metric_uid)
        } else if (metric_uid == 'gpsMetricTree'){
          payload?.gps?.push(...gpsMetrics.map(x=>x.unique_identifier))
        } else if (fpMetrics.filter(x=>x.unique_identifier == metric_uid).length > 0){
          payload?.fp_metrics?.push(metric_uid)
        } else if (metric_uid == 'fpMetricTree'){
          payload?.fp_metrics?.push(...fpMetrics.map(x=>x.unique_identifier))
        } else if (testingMetrics.filter(x=>x.unique_identifier == metric_uid).length > 0){
          payload?.testing?.push(metric_uid)
        } else if (metric_uid == 'testingMetricTree') {
          payload?.testing?.push(...testingMetrics.map(x=>x.unique_identifier))
        } else if (nutritionMetrics.filter(x=>x.unique_identifier == metric_uid).length > 0){
          payload?.nutrition?.push(metric_uid)
        } else if (metric_uid == 'nutritionMetricTree') {
          payload?.nutrition?.push(...nutritionMetrics.map(x=>x.unique_identifier))
        } else if (cognitionMetrics.filter(x=>x.unique_identifier == metric_uid).length > 0){
          payload?.cognition?.push(metric_uid)
        } else if (metric_uid == 'cognitionMetricTree') {
          payload?.cognition?.push(...cognitionMetrics.map(x=>x.unique_identifier))
        }
      })

      let agg = "Avg"
      if (filterValue == "average"){
        agg = "Avg"
      } else if (filterValue == "best"){
        agg = "best"
      } else if (filterValue == "total"){
        agg = "Sum"
      }


      const response = await trainingAPI.getLeaderBoardPDF(
        startDate, 
        endDate, 
        agg, 
        filterTeam, 
        filterGroups, 
        payload, 
        lightMode,
        filterPlayerCount,
        groupBy,
        orientation
      );
      downloadBlob(response.data, 'Leaderboard.pdf'); 
      message.success("File Downloaded!")
    } catch (err) {
      console.error(err);
      message.error("Error downloading file")
    }
    setIsLoading(false);
  }


  //Modify leaderBoardData depends on changes of factors, [fixedFilter, filterValue, leaderBoardData, FilterTopPlayerNumber]
  useEffect(() => {
    let filteredData = filterLeaderBoardData(leaderBoardData, fixedFilter);
    let selectedMetricObjs = [];

    for (const metricType in all_metric_types){
      for (let i = 0; i < all_metric_types[metricType].length; i++) {
        const metricObj = all_metric_types[metricType][i];
        if (fixedFilter.includes(metricObj.unique_identifier)) {
          selectedMetricObjs.push(metricObj);
        }
      }
    }
    
    setFilteredData(filteredData.filter((e) => {
      const metric = selectedMetricObjs.find((metric) => metric.name === e.title);
      if (metric) {
        if (metric?.metric_type == "gps" && metric?.visible) return e;
        else return e;
      }
    }));
  }, [fixedFilter, filterValue, leaderBoardData, filterPlayerCount]);

  return (
    <>
      <Navbar />
      <HelpButton page="Leaderboard" />
      <div className="leaderboard-content">
        <FloatButton.BackTop style={{ insetInlineEnd: "auto", insetInlineStart: 24 }} />
        <div className="search-divider"></div>
        <div
          style={{
            textAlign: "center",
          }}
        >
          <Space>
            <RangePicker onChange={handleDateRangeChange} />
            <TreeSelect 
              {...tProps} 
              filterTreeNode={(input, node) =>
                node.title.toLowerCase().includes(input.toLowerCase())
              }
            />
            <Select
              showSearch
              value={filterPlayerCount}
              style={{ width: 100 }}
              options={top_x_options}
              onChange={handlePlayerCountChange}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              optionFilterProp="label"
            >
            </Select>
            <PositionSelect
              value={filterTeam}
              handleChange={handleFilterTeam}
              options={positions}
              width={110}
              placeholder="Full Team"
              sport={sport}
            />
            <Select
              showSearch
              allowClear
              placeholder="All Groups"
              style={{ width: 110 }}
              options={playerGroupsOption}
              onChange={handleFilterPlayerGroup}   
              mode="multiple"           
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />

            <Select
              showSearch
              allowClear
              placeholder="Group By"
              style={{ width: 110 }}

              options={[
                {'label': 'Positions', 'value': 'position'},
                {'label': 'My Groups', 'value': 'player_groups'},
              ]}
              onChange={(value) => setGroupBy(value)}
              value={groupBy}
            />

            <div>
              <Radio.Group
                value={filterValue}
                onChange={handleFilterValueChange}
              >
                <Radio.Button value="average">Avg</Radio.Button>
                <Radio.Button value="total">Sum</Radio.Button>
                <Radio.Button value="best">Best</Radio.Button>
              </Radio.Group>
            </div>  
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>

            <Popconfirm
              placement="bottomRight"
              title={popConfirmContent}
              onConfirm={handleExportToPDF}
              okText="Yes"
              cancelText="No"
              >
              <Button type="primary">
              Export to PDF
              </Button>
            </Popconfirm>
            
            <Spin spinning={isLoading} />
          </Space>
        </div>
        <div>
          <Space style={{ flexWrap: "wrap", marginBottom: 50 }}>
            {filteredData.filter(x => x.title !== undefined).map((item, index) => (<>
              <div
                key={`${item}-${index}`} // Add a unique key for each mapped element
                style={{
                  marginTop: 50,
                  marginLeft: 50,
                  marginRight: 50,
                }}
              >
                <h5>{item.title}</h5>
                {item.subtitle ? <h6>{item.subtitle}</h6> : null}
                <Table
                  columns={columns}
                  dataSource={item.rows || []}
                  pagination={false}
                  bordered
                />
              </div>
            </>))}
          </Space>
        </div>
      </div>
    </>
  );
};

export default Leaderboard;
